import { useMemo } from "react";

import { useInsightsContext } from "src/pages/decision-engines/insights/insights-context";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";
import { ChannelOptionsMap } from "src/pages/decision-engines/utils";
import { getCampaignIdsFromFlowMessages } from "@hightouch/lib/query/visual/analytics/decision-engine-utils";

import { TIME_BUCKET_LABELS } from "@hightouch/lib/customer-data/decision-engine/send-insights/constants";
import type { DecisionEngineChannelType } from "@hightouch/lib/customer-data/decision-engine/types";

import type { SendInsightDimension, LabelResolver } from "./types";

interface ChannelLookup {
  type: string;
  label: string | null;
  destinationName: string | null;
}

export function useLabelResolver(flowId: string): LabelResolver {
  const { flowMessages } = useInsightsContext();
  const { channels } = useDecisionEngineContext();

  const messageById = useMemo(() => {
    const map = new Map<
      string,
      {
        name: string;
        channelType?: DecisionEngineChannelType;
        campaignIds: string[];
      }
    >();
    for (const fm of flowMessages ?? []) {
      const rawType = fm.message.channel?.type;
      const channelType =
        rawType && rawType in ChannelOptionsMap
          ? (rawType as DecisionEngineChannelType)
          : undefined;
      const campaignIds = [
        ...new Set(getCampaignIdsFromFlowMessages([{ config: fm.config }])),
      ];
      map.set(fm.message.id, {
        name: fm.message.name,
        channelType,
        campaignIds,
      });
    }
    return map;
  }, [flowMessages]);

  const channelById = useMemo(() => {
    const map = new Map<string, ChannelLookup>();
    for (const ch of channels) {
      map.set(ch.id, {
        type: ch.type,
        label: ch.label,
        destinationName: ch.destination.name,
      });
    }
    return map;
  }, [channels]);

  const typesWithDuplicates = useMemo(() => {
    const typeCounts = new Map<string, number>();
    for (const ch of channels) {
      if (!ch.label) {
        typeCounts.set(ch.type, (typeCounts.get(ch.type) ?? 0) + 1);
      }
    }
    return new Set(
      [...typeCounts.entries()]
        .filter(([, count]) => count > 1)
        .map(([type]) => type),
    );
  }, [channels]);

  return useMemo(
    () => (dimension: SendInsightDimension, rawValue: string | number) => {
      const str = String(rawValue);

      if (dimension === "message_id") {
        const msg = messageById.get(str);
        return {
          name: msg?.name ?? str,
          id: str,
          url: msg ? `/ai/flows/${flowId}/messages/${str}/overview` : undefined,
          channelType: msg?.channelType,
          campaignIds: msg?.campaignIds,
        };
      }

      if (dimension === "channel") {
        const ch = channelById.get(str);
        if (!ch) return { name: str, id: str };
        const typeLabel = ChannelOptionsMap[ch.type]?.label || ch.type;
        const name = ch.label
          ? ch.label
          : ch.destinationName && typesWithDuplicates.has(ch.type)
            ? `${typeLabel} (${ch.destinationName})`
            : typeLabel;
        const channelType =
          ch.type in ChannelOptionsMap
            ? (ch.type as DecisionEngineChannelType)
            : undefined;
        return {
          name,
          id: str,
          url: `/ai/configuration/channels/${str}`,
          channelType,
        };
      }

      if (dimension === "time_bucket") {
        return { name: TIME_BUCKET_LABELS[str] ?? str };
      }

      return { name: str };
    },
    [flowId, messageById, channelById, typesWithDuplicates],
  );
}
